import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router';
import { queryCache, useQuery } from 'react-query';
import { getAssessment } from 'src/api/assessment';
import { ProjectionVariant, QKeys } from 'src/api/types';
import Meta from 'src/components/Meta';
import { PageContainer, PageChartSkeleton, Spacer, PageCard, VariantValues, Divider, Poppins } from 'src/common';
import { useGaEventPageView } from '../../utils/gaEvent';
import { Head } from './comps';
import useVersion from 'src/hooks/useVersion';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { AuditSection, ExceedanceSection, RiskRegisterSection } from './sections';
import { OverviewSection } from './sections/OverviewSection';
import { ObjectKind, useAssessmentQuery } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql';
import ActionsSection from 'src/components/ActionsSection';
import { LABELS } from 'src/components/labels';
import Comments, { CommentsPageLayout } from 'src/components/Comments';
import ReviewsSection from 'src/components/ReviewsSection';
import { useAuth } from 'src/state/auth';

const Card = styled(PageCard)`
  padding-bottom: 0;
`;

const AssessmentPage = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [isLogarithmic, setIsLogarithmic] = useState(true);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const { versionId } = useVersion();
  const { setLocationPaths } = useActionsApp();
  const { features } = useAuth();

  const {
    data: { assessment } = {},
    error,
    loading,
    networkStatus,
  } = useAssessmentQuery({ variables: { id: assessmentId!, versionId } });
  const isLoading = isQueryLoading({ networkStatus, data: assessment, loading });

  useQuery([QKeys.Assessment, { assessmentId, version: versionId, variant: ProjectionVariant.live }], getAssessment);

  const showAllProjected = !!assessment?.scenarios?.flatMap((el) =>
    el.riskManagementPlans
      ?.filter((rmp) => rmp.control && rmp.control.status !== 'Abandoned' && rmp.control.status !== 'Retired')
      ?.flatMap((rmp) => rmp.modifiers)
      ?.filter((mod) => mod.type !== 'NOTE_TYPE'),
  )?.length;

  useEffect(() => {
    if (assessment) {
      setLocationPaths({ assessment: { id: assessment.id, name: assessment.name } });
    }
    return () => {
      setLocationPaths({ assessment: undefined });
    };
  }, [assessment]);

  useGaEventPageView('Cydea | Assessment');
  const showReviewsModule = !!features?.['review.iteration3'];

  if (isLoading)
    return (
      <PageContainer>
        <PageChartSkeleton />
      </PageContainer>
    );

  return (
    <>
      <Meta title={`Cydea | ${assessment?.name}`} feedbackScript />
      <CommentsPageLayout>
        <div className="page-content unstyled-scroll">
          <PageContainer>
            {assessment && (
              <Card>
                <Head
                  data={assessment}
                  isLogarithmic={isLogarithmic}
                  commentsPanel={{
                    isOpen: commentsOpen,
                    onToggle: setCommentsOpen,
                  }}
                  expectedLossComponent={
                    <VariantValues
                      flat
                      value={
                        showAllProjected
                          ? {
                              base: assessment?.AssessmentBaseRisk?.expectedLoss,
                              live: assessment?.AssessmentLiveRisk?.expectedLoss,
                              cpmplete: assessment?.AssessmentProjectedRisk?.expectedLoss,
                            }
                          : assessment?.AssessmentBaseRisk?.expectedLoss || 0
                      }
                      tooltipText={{
                        base: LABELS.riskBase,
                        live: LABELS.riskLive,
                        cpmplete: LABELS.riskComplete,
                      }}
                    />
                  }
                />
                <Spacer $px={20} />
                <Divider />
                <ExceedanceSection
                  data={assessment}
                  version={versionId}
                  showAllProjected={showAllProjected}
                  isLogarithmic={isLogarithmic}
                  setIsLogarithmic={setIsLogarithmic}
                />
                <Divider />
                <OverviewSection data={assessment} />
                <Divider />
                <RiskRegisterSection
                  assessmentId={assessment.id}
                  assessmentName={assessment.name}
                  showAllProjected={showAllProjected}
                />
                <Divider />
                <ActionsSection objectType={ObjectKind.Assessment} objectId={assessment.id} name={assessment.name} />
                <Divider />
                {showReviewsModule && (
                  <>
                    <ReviewsSection objectType={ObjectKind.Assessment} objectId={assessment.id} reviewDate={assessment.reviewDate} />
                    <Divider />
                  </>
                )}
                <AuditSection version={versionId} />
              </Card>
            )}
            {error && <Poppins color="error">{error.message}</Poppins>}
          </PageContainer>
        </div>
        {commentsOpen && assessment && (
          <div className="comments-panel open">
            <Comments
              onClose={() => setCommentsOpen(false)}
              eventData={{ objectType: ObjectKind.Assessment, objectId: assessment.id }}
              refetch={() => queryCache.invalidateQueries([QKeys.Audit])}
            />
          </div>
        )}
      </CommentsPageLayout>
    </>
  );
};

export default AssessmentPage;
